// The kernel's extraction engine stores the literal string "Not answerable"
// when a respondent's reply contained no extractable answer for the expected
// variable (an LLM convention, not a kernel enum). Display it as an explicit
// non-answer label so it doesn't read as something the respondent said.
export const NON_ANSWER_LABEL = 'User Did Not Answer';

export function mapNonAnswer(text) {
    if (typeof text === 'string' && text.trim().toLowerCase() === 'not answerable') {
        return NON_ANSWER_LABEL;
    }
    return text;
}

/**
 * The storage path an answer points at, when the answer IS a file upload.
 * A value_json can be the path, a JSON string holding it, or an object with it
 * nested under storagePath/gcs_path (optionally inside `metadata`).
 * Returns null when the answer is ordinary data.
 */
export function extractGcsPath(val) {
    if (val == null) return null;
    const raw = typeof val === 'string' ? val : JSON.stringify(val);
    const stripped = typeof raw === 'string' ? raw.trim().replace(/^"|"$/g, '') : raw;
    if (typeof stripped === 'string' && stripped.startsWith('gs://')) return stripped;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            const meta = parsed.metadata || parsed;
            return parsed.storagePath || parsed.gcs_path || meta.storagePath || meta.gcs_path || null;
        }
    } catch (_) { }
    return null;
}

/** The filename an upload path ends in — what the Results table shows. */
export function uploadFilename(gcsPath) {
    if (!gcsPath) return '';
    try {
        return decodeURIComponent(String(gcsPath).split('/').pop() || '') || String(gcsPath);
    } catch (_) {
        return String(gcsPath).split('/').pop() || String(gcsPath);
    }
}

function resultValueText(row) {
    if (row?.answer_text != null && row.answer_text !== '') return mapNonAnswer(String(row.answer_text));
    const value = row?.value_json;
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return mapNonAnswer(String(value));
    }
    return JSON.stringify(value);
}

function responseValueText(value) {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return mapNonAnswer(String(value));
    }
    return JSON.stringify(value);
}

/**
 * Build Survey Space matrix cells from the deployment's session-level response
 * feed, then overlay any matching legacy result rows for per-answer confidence.
 *
 * The deployment response feed is the authoritative matrix source: it is keyed
 * by session_id and contains one row per take. The legacy results endpoint is
 * paginated by answer and recipient-keyed, so it cannot populate a large matrix
 * reliably on its own.
 */
export function buildMatrixCells({
    sessions = [],
    columns = [],
    deploymentResponses = [],
    resultsRows = [],
} = {}) {
    const cells = {};
    const responseBySession = new Map(
        deploymentResponses
            .filter(row => row?.session_id)
            .map(row => [String(row.session_id), row]),
    );
    const questionColumns = columns.filter(col => !col.system);

    const resultsByRecipient = {};
    const resultsBySession = {};
    for (const row of resultsRows) {
        if (row?.recipient_id) {
            (resultsByRecipient[String(row.recipient_id)] ||= []).push(row);
        }
        if (row?.session_id) {
            (resultsBySession[String(row.session_id)] ||= []).push(row);
        }
    }

    for (const session of sessions) {
        const sessionId = String(session?.session_id || '');
        if (!sessionId) continue;

        const response = responseBySession.get(sessionId);
        if (response) {
            for (const column of questionColumns) {
                if (!Object.prototype.hasOwnProperty.call(response, column.name)) continue;
                const text = responseValueText(response[column.name]);
                if (text === '') continue;
                cells[`${sessionId} ${column.name}`] = {
                    text,
                    confidence: response.confidence_weight != null
                        ? Number(response.confidence_weight)
                        : null,
                };
            }
        }

        // Overlay rows that can be joined exactly. This preserves per-answer
        // confidence without making the matrix depend on the results page limit.
        const matchingResults = [];
        for (const recipientId of [
            session?.participant_id,
            session?.responder_id,
            session?.recipient_id,
        ]) {
            if (recipientId && resultsByRecipient[String(recipientId)]) {
                matchingResults.push(...resultsByRecipient[String(recipientId)]);
            }
        }
        if (resultsBySession[sessionId]) matchingResults.push(...resultsBySession[sessionId]);
        for (const row of matchingResults) {
            if (!row?.variable_name) continue;
            cells[`${sessionId} ${row.variable_name}`] = {
                text: resultValueText(row),
                confidence: row.confidence != null ? Number(row.confidence) : null,
            };
        }

        const completionTime = response?.completion_time ?? session?.completion_time;
        if (completionTime != null) {
            cells[`${sessionId} __completion_time`] = {
                text: String(completionTime),
                confidence: null,
            };
        }
        const recipientGroup = response?.recipient_group ?? session?.recipient_group;
        if (recipientGroup) {
            cells[`${sessionId} __recipient_group`] = {
                text: String(recipientGroup),
                confidence: null,
            };
        }
        const status = response?.completion_status
            || session?.completion_status
            || session?.status
            || '';
        if (status) {
            cells[`${sessionId} __status`] = {
                text: String(status),
                confidence: null,
            };
        }
    }

    return cells;
}
